import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Grid,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputLabel,
  TextField,
  Chip,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  DragOverlay,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDivisionOptions, useTeamOptions } from '../../../state/master';
import { apiClient } from '../../../lib/apiClient';

type Props = {
  open: boolean;
  division: number;
  canEdit?: boolean;
  onClose: () => void;
  onSaved?: () => void | Promise<void>;
};

type TeamItem = {
  id: string;
  name: string;
};

const DraggableTeamItem: React.FC<{ item: TeamItem; containerId: string; canEdit?: boolean }> = ({ item, containerId, canEdit = true }) => {
  const { palette } = useTheme();
  const isDark = palette.mode === 'dark';
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
    data: { containerId },
    disabled: !canEdit,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        border: isDark ? '1px solid #334155' : '1px solid #cfcfcf',
        borderRadius: 1,
        padding: '8px 10px',
        backgroundColor: isDark ? '#1e293b' : '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: canEdit ? (isDragging ? 'grabbing' : 'grab') : 'default',
        userSelect: 'none',
        boxShadow: isDragging ? '0 4px 8px rgba(0,0,0,0.12)' : 'none',
      }}
      {...(canEdit ? attributes : {})}
      {...(canEdit ? listeners : {})}
    >
      <Typography variant="body2">{item.name}</Typography>
      <Box
        sx={{
          width: 14,
          height: 14,
          borderRadius: 0.5,
          border: isDark ? '1px solid #334155' : '1px solid #bdbdbd',
          display: 'inline-block',
          backgroundColor: isDark ? '#243044' : '#fafafa',
        }}
      />
    </Box>
  );
};

const DroppableArea: React.FC<{ id: string; active: boolean; children: React.ReactNode }> = ({ id, active, children }) => {
  const { setNodeRef } = useDroppable({ id });
  return (
    <Box
      ref={setNodeRef}
      sx={{
        borderRadius: 1,
        outline: active ? '2px solid #2e7d32' : '2px solid transparent',
        outlineOffset: 2,
      }}
    >
      {children}
    </Box>
  );
};

const TeamClassficationDialog: React.FC<Props> = ({ open, division, canEdit = true, onClose, onSaved }) => {
  const { palette } = useTheme();
  const isDark = palette.mode === 'dark';
  const [classifications, setClassifications] = React.useState<Array<{ id: number; name: string; __ui_new?: boolean; teamIds: string[] }>>([]);
  const { divisionOptions } = useDivisionOptions();
  const { teamOptions } = useTeamOptions();
  const [allTeams, setAllTeams] = React.useState<TeamItem[]>([]);
  const [unassignedTeamIds, setUnassignedTeamIds] = React.useState<string[]>([]);
  const nextIdRef = React.useRef(1);
  const classificationsRef = React.useRef(classifications);
  const unassignedRef = React.useRef(unassignedTeamIds);
  const initKeyRef = React.useRef<string>('');
  const [addDialogOpen, setAddDialogOpen] = React.useState(false);
  const [addName, setAddName] = React.useState('');
  const [addNameError, setAddNameError] = React.useState<string>('');
  const [submitting, setSubmitting] = React.useState(false);
  const [loadingClassification, setLoadingClassification] = React.useState(false);
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor));
  const [activeId, setActiveId] = React.useState<string | null>(null);
  const [overContainerId, setOverContainerId] = React.useState<string | null>(null);

  const normalizeClassName = React.useCallback((raw: string): string => {
    return String(raw ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
  }, []);

  const duplicateNameKeySet = React.useMemo(() => {
    const counts = new Map<string, number>();
    classifications.forEach((row) => {
      const key = normalizeClassName(row.name);
      if (!key) return;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    });
    const set = new Set<string>();
    counts.forEach((count, key) => {
      if (count >= 2) set.add(key);
    });
    return set;
  }, [classifications, normalizeClassName]);

  const hasDuplicateNames = duplicateNameKeySet.size > 0;

  const divisionLabel = React.useMemo(() => {
    const found = (divisionOptions ?? []).find(
      (d: any) => Number((d as any)?.department_id) === Number(division)
    );
    return String((found as any)?.label ?? (found as any)?.value ?? '');
  }, [divisionOptions, division]);

  React.useEffect(() => {
    if (!open) {
      initKeyRef.current = '';
      return;
    }
    const list = (Array.isArray(teamOptions) ? teamOptions : [])
      .filter((t: any) => Number((t as any)?.department_id) === Number(division))
      .map((t: any) => ({
        id: String((t as any)?.team_id ?? (t as any)?.id ?? (t as any)?.value ?? ''),
        name: String((t as any)?.label ?? (t as any)?.value ?? ''),
      }))
      .filter((t: TeamItem) => t.id && t.name);
    if (list.length === 0) return;
    setAllTeams(list);
    const key = String(division);
    if (initKeyRef.current !== key) {
      setUnassignedTeamIds(list.map((t) => t.id));
      initKeyRef.current = key;
    }
  }, [teamOptions, division, open]);

  React.useEffect(() => {
    classificationsRef.current = classifications;
  }, [classifications]);

  React.useEffect(() => {
    unassignedRef.current = unassignedTeamIds;
  }, [unassignedTeamIds]);

  React.useEffect(() => {
    if (!open) return;

    let cancelled = false;
    const run = async () => {
      try {
        setLoadingClassification(true);
        const res = await apiClient.get('/get_team_classfication', {
          params: { department_id: Number(division) },
        });
        if (cancelled) return;

        const data = res?.data ?? {};
        const rawParams = (data as any)?.new_params;
        const paramsList = Array.isArray(rawParams)
          ? rawParams
          : rawParams && typeof rawParams === 'object'
            ? [rawParams]
            : [];

        const nextClassifications = paramsList
          .map((row: any, index: number) => {
            const idNum = Number((row as any)?.class_id);
            const fallbackId = index + 1;
            const teamIds = Array.isArray((row as any)?.team_ids)
              ? (row as any).team_ids
                  .map((id: any) => String(id))
                  .filter((id: string) => id.length > 0)
              : [];
            return {
              id: Number.isFinite(idNum) ? idNum : fallbackId,
              name: String((row as any)?.classification_name ?? ''),
              teamIds,
              sortOrder: Number((row as any)?.sort_order),
            };
          })
          .sort((a, b) => {
            const sa = Number.isFinite(a.sortOrder) ? a.sortOrder : Number.MAX_SAFE_INTEGER;
            const sb = Number.isFinite(b.sortOrder) ? b.sortOrder : Number.MAX_SAFE_INTEGER;
            return sa - sb;
          })
          .map(({ sortOrder, ...row }: any) => row);

        setClassifications(nextClassifications);

        const maxId = nextClassifications.reduce((max: number, row: any) => Math.max(max, Number(row.id) || 0), 0);
        nextIdRef.current = Math.max(1, maxId + 1);

        const apiUnassigned = Array.isArray((data as any)?.unassigned_team_ids)
          ? (data as any).unassigned_team_ids
              .map((id: any) => String(id))
              .filter((id: string) => id.length > 0)
          : [];
        setUnassignedTeamIds(apiUnassigned);
      } catch (error) {
        console.error('get_team_classfication error', error);
      } finally {
        if (!cancelled) setLoadingClassification(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [open, division]);

  const handleAddClick = () => {
    setAddName('');
    setAddNameError('');
    setAddDialogOpen(true);
  };

  const handleAddDialogClose = () => {
    setAddNameError('');
    setAddDialogOpen(false);
  };

  const handleAddDialogOk = () => {
    const name = addName.trim();
    if (!name) return;
    const key = normalizeClassName(name);
    const duplicated = classifications.some(
      (row) => normalizeClassName(row.name) === key
    );
    if (duplicated) {
      setAddNameError('同じ分類名は追加できません');
      return;
    }
    const nextId = nextIdRef.current++;
    setClassifications((prev) => [...prev, { id: nextId, name, __ui_new: true, teamIds: [] }]);
    setAddNameError('');
    setAddDialogOpen(false);
  };

  const handleChangeName = (id: number, value: string) => {
    setClassifications((prev) => prev.map((row) => (row.id === id ? { ...row, name: value } : row)));
  };

  const handleDelete = (id: number) => {
    setClassifications((prev) => prev.filter((row) => row.id !== id));
    const target = classificationsRef.current.find((c) => c.id === id);
    if (!target || target.teamIds.length === 0) return;
    setUnassignedTeamIds((prev) => {
      const set = new Set(prev);
      target.teamIds.forEach((t) => set.add(t));
      return Array.from(set);
    });
  };

  const findContainer = (id: string) => {
    if (id === 'unassigned') return 'unassigned';
    if (unassignedRef.current.includes(id)) return 'unassigned';
    const hit = classificationsRef.current.find((c) => c.teamIds.includes(id));
    return hit ? `class-${hit.id}` : undefined;
  };

  const handleDragStart = (event: any) => {
    setActiveId(String(event.active.id));
  };

  const handleDragOver = (event: any) => {
    const overId = event.over?.id;
    if (!overId) {
      setOverContainerId(null);
      return;
    }
    const overIdString = String(overId);
    const overContainer = (overIdString === 'unassigned' || overIdString.startsWith('class-'))
      ? overIdString
      : findContainer(overIdString);
    setOverContainerId(overContainer ?? null);
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    setActiveId(null);
    setOverContainerId(null);
    if (!over) return;

    const activeId = String(active.id);
    const overId = String(over.id);
    const activeContainer = active.data.current?.containerId || findContainer(activeId);
    const overContainer = (overId === 'unassigned' || overId.startsWith('class-'))
      ? overId
      : findContainer(overId);
    if (!activeContainer || !overContainer) return;

    // Same container: reorder
    if (activeContainer === overContainer) {
      if (overId === activeContainer) return;
      if (activeContainer === 'unassigned') {
        setUnassignedTeamIds((items) => {
          const oldIndex = items.indexOf(activeId);
          const newIndex = items.indexOf(overId);
          if (oldIndex < 0 || newIndex < 0) return items;
          return arrayMove(items, oldIndex, newIndex);
        });
      } else {
        const classId = Number(activeContainer.replace('class-', ''));
        setClassifications((prev) =>
          prev.map((c) => {
            if (c.id !== classId) return c;
            const oldIndex = c.teamIds.indexOf(activeId);
            const newIndex = c.teamIds.indexOf(overId);
            if (oldIndex < 0 || newIndex < 0) return c;
            return { ...c, teamIds: arrayMove(c.teamIds, oldIndex, newIndex) };
          })
        );
      }
      return;
    }

    // Move between containers
    const currentUnassigned = unassignedRef.current;
    const currentClassifications = classificationsRef.current;
    let nextUnassigned = currentUnassigned.filter((x) => x !== activeId);
    let nextClassifications = currentClassifications.map((c) => ({
      ...c,
      teamIds: c.teamIds.filter((x) => x !== activeId),
    }));

    if (overContainer === 'unassigned') {
      if (!nextUnassigned.includes(activeId)) nextUnassigned = [...nextUnassigned, activeId];
    } else {
      const classId = Number(overContainer.replace('class-', ''));
      nextClassifications = nextClassifications.map((c) =>
        c.id === classId && !c.teamIds.includes(activeId)
          ? { ...c, teamIds: [...c.teamIds, activeId] }
          : c
      );
    }

    setUnassignedTeamIds(nextUnassigned);
    setClassifications(nextClassifications);
  };

  const handleDragCancel = () => {
    setActiveId(null);
    setOverContainerId(null);
  };

  const clearDraftGrouping = React.useCallback(() => {
    setClassifications([]);
    setUnassignedTeamIds(allTeams.map((team) => team.id));
  }, [allTeams]);

  const handleCancel = React.useCallback(() => {
    clearDraftGrouping();
    onClose();
  }, [clearDraftGrouping, onClose]);

  const handleSubmit = async () => {
    if (hasDuplicateNames) return;

    const payload = {
      department_id: Number(division),
      new_params: classifications.map((row, index) => ({
        classification_name: row.name.trim(),
        sort_order: index + 1,
        team_ids: row.teamIds
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id)),
      })),
      unassigned_team_ids: unassignedTeamIds
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id)),
    };

    try {
      setSubmitting(true);
      await apiClient.post('/refresh_team_classfication', payload);
      if (onSaved) {
        await onSaved();
      }
      clearDraftGrouping();
      onClose();
    } catch (error) {
      console.error('refresh_team_classfication post error', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleCancel} fullWidth maxWidth="md">
      <DialogTitle>チーム分類設定 - {divisionLabel || `課${division}`}</DialogTitle>
      <DialogContent>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 8 }}>
              <Box sx={{ border: isDark ? '1px solid #334155' : '1px solid #e0e0e0', borderRadius: 1, p: 2, minHeight: 240 }}>
                <InputLabel sx={{ mb: 1, fontWeight: 'bold' }}>チーム分類一覧</InputLabel>
                <TableContainer component={Paper} sx={{ overflowX: 'hidden' }}>
                  <Table stickyHeader aria-label="team classification table" sx={{ tableLayout: 'fixed', width: '100%' }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>分類名</TableCell>
                        <TableCell>チーム一覧</TableCell>
                        <TableCell sx={{ width: 120, minWidth: 120, maxWidth: 120 }}>
                          <Button
                            variant="contained"
                            size="small"
                            color="success"
                            onClick={handleAddClick}
                            disabled={!canEdit}
                            sx={{ minWidth: 64, width: 64, px: 1, flexShrink: 0, whiteSpace: 'nowrap' }}
                          >
                            追加
                          </Button>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {classifications.length === 0 && (
                        <TableRow>
                        <TableCell colSpan={3}>
                          <Typography variant="body2" color="text.secondary">
                            追加項目は後ほど表示されます
                          </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                      {classifications.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell>
                            {(() => {
                              const duplicated = duplicateNameKeySet.has(
                                normalizeClassName(row.name)
                              );
                              return (
                            <TextField
                              fullWidth
                              size="small"
                              value={row.name}
                              onChange={(event) => handleChangeName(row.id, event.target.value)}
                              placeholder="分類名を入力"
                              disabled={!canEdit}
                              error={duplicated}
                              helperText={duplicated ? '同じ分類名は登録できません' : ' '}
                              InputProps={{
                                sx: row.__ui_new ? { pr: 0.5 } : undefined,
                                endAdornment: row.__ui_new ? (
                                  <InputAdornment position="end" sx={{ ml: 0.5 }}>
                                    <Tooltip title="新規追加行">
                                      <Chip
                                        icon={<NewReleasesIcon sx={{ fontSize: 14 }} />}
                                        label="New"
                                        size="small"
                                        color="success"
                                        variant="outlined"
                                        sx={{
                                          height: 18,
                                          '& .MuiChip-label': { px: 0.5, fontSize: '0.65rem' },
                                          '& .MuiChip-icon': { ml: 0.4, mr: -0.2 },
                                        }}
                                      />
                                    </Tooltip>
                                  </InputAdornment>
                                ) : undefined,
                              }}
                            />
                              );
                            })()}
                          </TableCell>
                          <TableCell>
                            <SortableContext items={row.teamIds} strategy={verticalListSortingStrategy}>
                              <DroppableArea id={`class-${row.id}`} active={overContainerId === `class-${row.id}`}>
                                <Box
                                  sx={{
                                    border: isDark ? '1px solid #334155' : '1px solid #e0e0e0',
                                    borderRadius: 1,
                                    minHeight: 40,
                                    backgroundColor: isDark ? '#1e293b' : '#fff',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1,
                                    p: 1,
                                  }}
                                >
                                  {row.teamIds.length === 0 && (
                                    <Typography variant="caption" color="text.secondary">
                                      ここにドラッグ
                                    </Typography>
                                  )}
                                  {row.teamIds.map((id) => {
                                    const team = allTeams.find((t) => t.id === id);
                                    if (!team) return null;
                                    return <DraggableTeamItem key={team.id} item={team} containerId={`class-${row.id}`} canEdit={canEdit} />;
                                  })}
                                </Box>
                              </DroppableArea>
                            </SortableContext>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="contained"
                              size="small"
                              color="error"
                              onClick={() => handleDelete(row.id)}
                              disabled={!canEdit}
                              sx={{ minWidth: 64, width: 64, px: 1, flexShrink: 0, whiteSpace: 'nowrap' }}
                            >
                              削除
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Box sx={{ border: isDark ? '1px solid #334155' : '1px solid #e0e0e0', borderRadius: 1, p: 2, minHeight: 240 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  未割当チーム一覧
                </Typography>
                <SortableContext items={unassignedTeamIds} strategy={verticalListSortingStrategy}>
                  <DroppableArea id="unassigned" active={overContainerId === 'unassigned'}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        maxHeight: 8 * 48,
                        overflowY: 'auto',
                        pr: 0.5,
                      }}
                    >
                      {unassignedTeamIds.map((id) => {
                        const team = allTeams.find((t) => t.id === id);
                        if (!team) return null;
                        return <DraggableTeamItem key={team.id} item={team} containerId="unassigned" canEdit={canEdit} />;
                      })}
                    </Box>
                  </DroppableArea>
                </SortableContext>
              </Box>
            </Grid>
          </Grid>
          <DragOverlay>
            {activeId ? (
              <Box
                sx={{
                  border: isDark ? '1px solid #334155' : '1px solid #cfcfcf',
                  borderRadius: 1,
                  padding: '8px 10px',
                  backgroundColor: isDark ? '#1e293b' : '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  boxShadow: '0 8px 16px rgba(0,0,0,0.18)',
                  minWidth: 160,
                }}
              >
                <Typography variant="body2">
                  {allTeams.find((t) => t.id === activeId)?.name ?? ''}
                </Typography>
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: 0.5,
                    border: isDark ? '1px solid #334155' : '1px solid #bdbdbd',
                    display: 'inline-block',
                    backgroundColor: isDark ? '#243044' : '#fafafa',
                  }}
                />
              </Box>
            ) : null}
          </DragOverlay>
        </DndContext>
      </DialogContent>
      <DialogActions>
        {hasDuplicateNames && (
          <Typography variant="caption" color="error" sx={{ mr: 'auto', ml: 1 }}>
            同じ分類名があるため登録できません
          </Typography>
        )}
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canEdit || submitting || loadingClassification || hasDuplicateNames}
        >
          {submitting ? <CircularProgress size={18} color="inherit" /> : '登録'}
        </Button>
        <Button variant="outlined" color="secondary" onClick={handleCancel} disabled={submitting || loadingClassification}>
          キャンセル
        </Button>
      </DialogActions>
      <Dialog open={addDialogOpen} onClose={handleAddDialogClose} fullWidth maxWidth="sm">
        <DialogTitle>分類を追加</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="分類名"
            value={addName}
            onChange={(event) => {
              setAddName(event.target.value);
              if (addNameError) setAddNameError('');
            }}
            error={Boolean(addNameError)}
            helperText={addNameError || ' '}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="success" onClick={handleAddDialogOk} disabled={!canEdit}>
            追加
          </Button>
          <Button variant="outlined" color="secondary" onClick={handleAddDialogClose}>キャンセル</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default TeamClassficationDialog;
